import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Lock, LockOpen, X } from 'lucide-react';
import {
  ApiError,
  SURVEY_CATEGORY_LABEL,
  api,
  canEdit,
  type AuthUser,
  type Survey,
  type SurveyAnswer,
  type SurveyRow,
} from '@/lib/api';

const RANK_ORDER: Record<string, number> = { R5: 5, R4: 4, R3: 3, R2: 2, R1: 1 };

export default function SurveyDetail({ user }: { user: AuthUser }) {
  const { id } = useParams();
  const surveyId = Number(id);
  const editable = canEdit(user.role);

  const detailQuery = useQuery({
    queryKey: ['survey', surveyId],
    queryFn: () => api.getSurvey(surveyId),
    enabled: !!surveyId,
  });

  if (detailQuery.isLoading) {
    return <div className="text-slate-400">Loading…</div>;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return <div className="text-red-400">Survey not found.</div>;
  }

  const { survey, rows } = detailQuery.data;
  const readOnly = !editable || survey.status === 'closed';
  const sortedRows = [...rows].sort((a, b) => {
    const r = (RANK_ORDER[b.player.rank] ?? 0) - (RANK_ORDER[a.player.rank] ?? 0);
    if (r) return r;
    return a.player.name.localeCompare(b.player.name);
  });

  return (
    <div className="space-y-4">
      <Link
        to="/surveys"
        className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft size={14} /> Back to surveys
      </Link>

      <SurveyHeader survey={survey} editable={editable} />

      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2 text-left">Player</th>
              <th className="px-3 py-2 text-left">Rank</th>
              <th className="px-3 py-2 text-left">Answer</th>
              <th className="px-3 py-2 text-left">Notes</th>
              <th className="px-3 py-2 text-left">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {sortedRows.map((row) => (
              <ResponseRow
                key={row.player.id}
                surveyId={surveyId}
                row={row}
                readOnly={readOnly}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Style />
    </div>
  );
}

function SurveyHeader({
  survey,
  editable,
}: {
  survey: Survey;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      api.updateSurvey(survey.id, {
        status: survey.status === 'open' ? 'closed' : 'open',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['survey', survey.id] });
      qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
  const { yes, no, pending, total } = survey.counts;
  const answered = yes + no;
  const pct = total > 0 ? (answered / total) * 100 : 0;

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1">
          <h1 className="text-xl font-semibold">{survey.title}</h1>
          {survey.description && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">
              {survey.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
            <span>Created {survey.createdAt.slice(0, 10)}</span>
            <span
              className={
                survey.status === 'open'
                  ? 'rounded bg-emerald-700/50 px-2 py-0.5 text-emerald-100'
                  : 'rounded bg-slate-700 px-2 py-0.5 text-slate-300'
              }
            >
              {survey.status}
            </span>
            <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">
              {SURVEY_CATEGORY_LABEL[survey.category]}
            </span>
            {survey.closedAt && (
              <span>Closed {survey.closedAt.slice(0, 10)}</span>
            )}
          </div>
        </div>
        {editable && (
          <button
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
            className="flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {survey.status === 'open' ? (
              <>
                <Lock size={14} /> Close survey
              </>
            ) : (
              <>
                <LockOpen size={14} /> Reopen
              </>
            )}
          </button>
        )}
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
        <span className="tabular-nums text-emerald-300">{yes} yes</span>
        <span className="tabular-nums text-red-300">{no} no</span>
        <span className="tabular-nums text-slate-500">{pending} pending</span>
        <span className="ml-auto tabular-nums text-slate-500">
          {answered} / {total}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-slate-800">
        <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
      </div>
    </section>
  );
}

function ResponseRow({
  surveyId,
  row,
  readOnly,
}: {
  surveyId: number;
  row: SurveyRow;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(row.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotes(row.notes ?? '');
  }, [row.notes, row.player.id]);

  const save = useMutation({
    mutationFn: (patch: { answer?: SurveyAnswer; notes?: string | null }) =>
      api.setSurveyResponse(surveyId, row.player.id, {
        answer: patch.answer ?? row.answer,
        notes: patch.notes !== undefined ? patch.notes : (row.notes ?? null),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['survey', surveyId] });
      qc.invalidateQueries({ queryKey: ['surveys'] });
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to save'),
  });

  const displayName = row.player.displayName?.trim() || row.player.name;

  return (
    <tr className="hover:bg-slate-900/40">
      <td className="px-3 py-2">
        <div className="font-medium">{displayName}</div>
        {row.player.displayName && row.player.displayName !== row.player.name && (
          <div className="text-xs text-slate-500">{row.player.name}</div>
        )}
      </td>
      <td className="px-3 py-2 text-slate-400">{row.player.rank}</td>
      <td className="px-3 py-2">
        <AnswerToggle
          value={row.answer}
          disabled={readOnly || save.isPending}
          onChange={(answer) => save.mutate({ answer })}
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={notes}
          disabled={readOnly}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            const trimmed = notes.trim();
            const currentNotes = row.notes ?? '';
            if (trimmed !== currentNotes) {
              save.mutate({ notes: trimmed ? trimmed : null });
            }
          }}
          placeholder={readOnly ? '' : 'Add notes…'}
          className="input"
        />
        {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500 tabular-nums">
        {row.respondedAt ? row.respondedAt.slice(0, 16).replace('T', ' ') : '—'}
      </td>
    </tr>
  );
}

function AnswerToggle({
  value,
  disabled,
  onChange,
}: {
  value: SurveyAnswer;
  disabled: boolean;
  onChange: (v: SurveyAnswer) => void;
}) {
  const options: { key: SurveyAnswer; label: string; icon: React.ReactNode; active: string }[] = [
    {
      key: 'yes',
      label: 'Yes',
      icon: <Check size={14} />,
      active: 'bg-emerald-700/60 text-emerald-100 border-emerald-600',
    },
    {
      key: 'no',
      label: 'No',
      icon: <X size={14} />,
      active: 'bg-red-700/60 text-red-100 border-red-600',
    },
    {
      key: 'no_answer',
      label: '—',
      icon: null,
      active: 'bg-slate-700 text-slate-200 border-slate-600',
    },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded border border-slate-700">
      {options.map((o) => {
        const isActive = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            disabled={disabled}
            onClick={() => !isActive && onChange(o.key)}
            className={`flex items-center gap-1 px-2 py-1 text-xs disabled:opacity-50 ${
              isActive ? o.active : 'text-slate-400 hover:bg-slate-800'
            }`}
            title={o.key === 'no_answer' ? 'No answer' : o.label}
          >
            {o.icon}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
